import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { dateKey } from "./scheduleUtils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad2 = (n) => String(n).padStart(2, "0");
const cellKey = (year, monthIndex, date) => `${year}-${pad2(monthIndex + 1)}-${pad2(date)}`;
const monthLabel = (monthStart) => new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(monthStart);

function buildWeeks(monthStart) {
  const year = monthStart.getFullYear();
  const monthIndex = monthStart.getMonth();
  const startOffset = new Date(year, monthIndex, 1).getDay();
  const weeks = [];
  let cursor = new Date(year, monthIndex, 1 - startOffset);
  for (let week = 0; week < 6; week++) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push({ key: cellKey(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()), date: cursor.getDate(), inMonth: cursor.getMonth() === monthIndex });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    }
    weeks.push(days);
  }
  return weeks;
}

export function startOfMonth(value = new Date()) { return new Date(value.getFullYear(), value.getMonth(), 1); }
export function shiftMonth(monthStart, delta) { return new Date(monthStart.getFullYear(), monthStart.getMonth() + delta, 1); }

/**
 * Full month-grid calendar. Each in-range day shows an open-spot count derived from `sessions`;
 * days with no sessions are inert (not clickable), matching the client's live booking calendar.
 */
export function MonthCalendar({ sessions, month, onMonthChange, selectedKey, onSelectDate, emptyLabel = "No sessions" }) {
  const weeks = useMemo(() => buildWeeks(month), [month]);
  const todayKey = useMemo(() => dateKey(new Date()), []);
  const dayStats = useMemo(() => {
    const map = new Map();
    sessions.forEach((session) => {
      const key = dateKey(session.startsAt);
      const entry = map.get(key) || { count: 0, open: 0 };
      entry.count += 1;
      entry.open += Math.max(0, session.capacity - (session.occupancy?.booked || 0));
      map.set(key, entry);
    });
    return map;
  }, [sessions]);

  return (
    <div className="month-calendar">
      <div className="month-calendar-head">
        <button type="button" className="month-calendar-nav" onClick={() => onMonthChange(shiftMonth(month, -1))} aria-label="Previous month"><ChevronLeft size={18} /></button>
        <h3>{monthLabel(month)}</h3>
        <button type="button" className="month-calendar-nav" onClick={() => onMonthChange(shiftMonth(month, 1))} aria-label="Next month"><ChevronRight size={18} /></button>
      </div>
      <div className="month-calendar-weekdays" aria-hidden="true">{WEEKDAYS.map((label) => <span key={label}>{label}</span>)}</div>
      <div className="month-calendar-grid" role="grid" aria-label="Session availability by day">
        {weeks.flat().map((cell) => {
          const stats = dayStats.get(cell.key);
          const hasSessions = Boolean(stats?.count);
          const isToday = cell.key === todayKey;
          const isSelected = cell.key === selectedKey;
          return (
            <button
              key={cell.key}
              type="button"
              role="gridcell"
              className={`month-calendar-cell${cell.inMonth ? "" : " is-outside"}${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}${hasSessions ? " has-sessions" : ""}`}
              disabled={!hasSessions}
              aria-current={isToday ? "date" : undefined}
              aria-selected={hasSessions ? isSelected : undefined}
              onClick={() => hasSessions && onSelectDate(cell.key)}
            >
              <span className="month-calendar-date">{cell.date}</span>
              {hasSessions && <span className="month-calendar-count">{stats.open > 0 ? `${stats.open} open` : "Full"}</span>}
            </button>
          );
        })}
      </div>
      {dayStats.size === 0 && <p className="month-calendar-empty">{emptyLabel}</p>}
    </div>
  );
}
